import { minorUnits } from '@charitystack/shared/currency';
import { useDisplayCurrency } from '../currency/DisplayCurrencyProvider';
import { displayFixed, formatAmountLabel } from './formatUtils';
import React, { useState, useEffect, useRef } from 'react';
import { formatWithCommas } from './formatUtils';

// The handle is a requestAnimationFrame id, so it is cancelled with
// cancelAnimationFrame. clearInterval would cancel whichever unrelated timer
// shares the number, such as posthog-js's batch-flush timer (CON-353).
const cancelAnimation = (animationRef) => {
  if (animationRef.current) {
    cancelAnimationFrame(animationRef.current);
    animationRef.current = null;
  }
};

export const TotalAmountBasisContext = React.createContext('usd');

export default function TotalAmountDisplay({ amount, elementID, className, align = 'right', basis }) {
  const ctx = useDisplayCurrency();
  const contextBasis = React.useContext(TotalAmountBasisContext);
  const displayTarget = ctx.converted && (basis ?? contextBasis) === 'usd' ? displayFixed(amount || 0, ctx) : amount;
  const [displayAmount, setDisplayAmount] = useState(0);
  const animationRef = useRef(null);
  // The value on screen, read by the effect without depending on it:
  // depending on displayAmount would restart the animation every frame.
  const displayAmountRef = useRef(0);

  // Format amount with proper currency formatting (includes commas for thousands)
  const formatAmount = (value) => {
    if (value === '' || value === 0 || isNaN(value)) {
      return '0.00';
    }
    return formatWithCommas(parseFloat(value).toFixed(2));
  };

  // Animate amount changes with stock ticker effect
  useEffect(() => {
    const showAmount = (value) => {
      displayAmountRef.current = value;
      setDisplayAmount(value);
    };

    if (displayTarget === '' || displayTarget === 0 || isNaN(displayTarget)) {
      showAmount(0);
      return;
    }

    const targetAmount = parseFloat(displayTarget);
    const currentAmount = displayAmountRef.current;

    // If no change, don't animate. The threshold is the display currency's own
    // minor unit (GREP-4124-4, Greptile 4288434434): a flat 0.01 is right for
    // USD's 2 decimals but hides every real change in a 3-decimal currency
    // (KWD 1.000 -> 1.003 never painted) and is too fine for a 0-decimal one.
    const skipThreshold = Math.pow(10, -minorUnits(ctx.displayCurrency));
    if (Math.abs(targetAmount - currentAmount) < skipThreshold) {
      return;
    }

    // Clear any existing animation
    cancelAnimation(animationRef);

    const startTime = Date.now();
    const duration = 200; // Animation duration in milliseconds
    const startAmount = currentAmount;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);

      const newAmount = startAmount + (targetAmount - startAmount) * easeOutQuart;
      showAmount(newAmount);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        animationRef.current = null;
        showAmount(targetAmount);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    // Cleanup function (also runs on unmount)
    return () => cancelAnimation(animationRef);
  }, [displayTarget]);

  return (
    <div className={align === 'left' ? 'text-left' : 'text-right'} id={`${elementID}-total-amount-display`}>
      <div
        className={`${className || 'dynamic-amount font-semibold text-gray-900'}`}
        style={{ fontSize: className ? undefined : '1.5rem' }}
      >
        {formatAmountLabel(ctx.converted ? displayAmount.toFixed(minorUnits(ctx.displayCurrency)) : displayAmount, ctx, v => `$${formatAmount(v)}`)}
      </div>
    </div>
  );
}
